/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getBranches, getDoctors } from '../../api';
import {
    adaptBranchesToSelector,
    adaptDoctorsToSelector,
    adaptSpecializationsToSelector,
    filterDoctorsBySelectedBranch,
    filterDoctorsBySelectedProfile,
    filterDoctorsBySelectedSpecialization,
    findDoctorById,
    getSpecializationsFromDoctors,
} from './utils';
import { formatFullDate } from '../../utils';

const defaultPickerStartDate = formatFullDate(new Date());

// eslint-disable-next-line consistent-return
// eslint-disable-next-line max-len
export const fetchBranches = createAsyncThunk(
    'filters/fetchBranches',
    async ({
        baseUrl,
        currentBranchId,
    }, { rejectWithValue }) => {
        try {
            const response = await (await getBranches())(baseUrl);
            // eslint-disable-next-line no-nested-ternary
            return currentBranchId ? typeof currentBranchId === 'string' ? response.data.filter(e => e.branchId === currentBranchId) : response.data.filter(e => currentBranchId?.some(id => id === e.branchId)) : response.data;// ?.slice(0, 1);
        } catch (err) {
            rejectWithValue(err);
            throw err;
        }
    },
);
// eslint-disable-next-line consistent-return
// eslint-disable-next-line max-len
export const fetchDoctors = createAsyncThunk('filters/fetchDoctors', async ({
    baseUrl,
    currentBranchId,
}, { rejectWithValue }) => {
    try {
        const response = await (await getDoctors())(baseUrl);
        return currentBranchId ? typeof currentBranchId === 'string' ? response.data.filter(e => e.branches.some(b => b === currentBranchId)) : response.data.filter(e => e.branches.some(b => currentBranchId?.some(id => id === b))) : response.data;
    } catch (err) {
        rejectWithValue(err);
        throw err;
    }
});

export const initState = {
    branches: [],
    branchesMetadata: [],
    doctors: [],
    specializations: [],
    doctorsMetadata: [],
    specializationsMetadata: [],
    selectedBranch: undefined,
    selectedDoctor: undefined,
    selectedSpecialization: undefined,
    isLoading: false,
    isBranchesLoading: true,
    isDoctorsLoading: true,
    error: undefined,
    date: defaultPickerStartDate,
};

export const filtersSlice = createSlice({
    name: 'filters',
    initialState: { ...initState },
    reducers: {
        selectBranch: (state, action) => {
            const selectedBranchId = action.payload;

            const doctors = filterDoctorsBySelectedBranch([...state.doctors], selectedBranchId);
            const doctorsMetadata = adaptDoctorsToSelector(doctors);
            const specializations = getSpecializationsFromDoctors(doctors);
            const specializationsMetadata = adaptSpecializationsToSelector(specializations);

            state.selectedBranch = selectedBranchId;
            state.doctorsMetadata = doctorsMetadata;
            state.specializationsMetadata = specializationsMetadata;
            state.selectedDoctor = '';
            state.selectedSpecialization = '';
            state.selectedProfileOption = false;
        },
        selectBranchSimplified: (state, action) => {
            const selectedBranchId = action.payload;
            const doctors = filterDoctorsBySelectedBranch([...state.doctors], selectedBranchId);
            const doctorsMetadata = adaptDoctorsToSelector(doctors);
            const specializations = getSpecializationsFromDoctors(doctors);
            const specializationsMetadata = adaptSpecializationsToSelector(specializations);

            state.selectedBranch = selectedBranchId;
            state.doctorsMetadata = doctorsMetadata;
            state.specializationsMetadata = specializationsMetadata;
            state.selectedProfileOption = false;
        },
        selectSpecialization: (state, action) => {
            const selectedSpecialization = action.payload;
            const selectedBranchId = state.selectedBranch;
            const { selectedProfileOption } = state;

            // noinspection JSUnusedAssignment
            let doctorsMetadata = [];
            if (selectedSpecialization) {
                let doctors = filterDoctorsBySelectedBranch([...state.doctors], selectedBranchId);
                doctors = filterDoctorsBySelectedSpecialization(doctors, selectedSpecialization);
                if (selectedProfileOption) {
                    doctors = filterDoctorsBySelectedProfile(doctors, selectedProfileOption);
                }
                doctorsMetadata = adaptDoctorsToSelector(doctors);
            } else {
                doctorsMetadata = adaptDoctorsToSelector(state.doctors);
            }

            state.selectedSpecialization = selectedSpecialization;
            state.doctorsMetadata = doctorsMetadata;
            state.selectedDoctor = undefined;
        },

        selectProfileOption: (state, action) => {
            const selectedProfileOption = action.payload;
            const selectedBranchId = state.selectedBranch;
            const { selectedSpecialization } = state;
            let doctorsMetadata;
            if (selectedProfileOption) {
                let doctors = [...state.doctors];
                if (selectedBranchId) {
                    doctors = filterDoctorsBySelectedBranch(doctors, selectedBranchId);
                }
                if (selectedSpecialization) {
                    doctors = filterDoctorsBySelectedSpecialization(doctors, selectedSpecialization);
                }
                doctors = filterDoctorsBySelectedProfile(doctors, selectedProfileOption);
                doctorsMetadata = adaptDoctorsToSelector(doctors);
            } else {
                let doctors = [...state.doctors];
                if (selectedSpecialization) {
                    doctors = filterDoctorsBySelectedSpecialization(doctors, selectedSpecialization);
                }
                doctorsMetadata = adaptDoctorsToSelector(doctors);
            }
            state.selectedProfileOption = selectedProfileOption;
            state.doctorsMetadata = doctorsMetadata;
            state.selectedDoctor = undefined;
        },
        selectDoctor: (state, action) => {
            const selectedDoctorId = action.payload;
            let targetDoctor = {};
            let { selectedSpecialization } = state;
            if (selectedDoctorId) {
                targetDoctor = findDoctorById(state.doctors, selectedDoctorId);
                selectedSpecialization = targetDoctor && targetDoctor.specialityId;
            }
            if (selectedSpecialization) {
                const doctors = filterDoctorsBySelectedSpecialization([...state.doctors], selectedSpecialization);
                state.doctorsMetadata = adaptDoctorsToSelector(doctors);
            }
            state.selectedDoctor = selectedDoctorId;
            state.selectedSpecialization = selectedSpecialization;
        },
        selectDate: (state, action) => {
            state.date = action.payload;
        },
        resetSelectedFilters: (state) => {
            state.selectedBranch = undefined;
            state.selectedDoctor = undefined;
            state.selectedSpecialization = undefined;
            state.selectedProfileOption = false;
            state.date = defaultPickerStartDate;
            state.doctorsMetadata = adaptDoctorsToSelector(state.doctors);
            state.specializationsMetadata = adaptSpecializationsToSelector(state.specializations);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBranches.pending, (state) => {
                state.isBranchesLoading = true;
                state.error = undefined;
            })
            .addCase(fetchBranches.fulfilled, (state, action) => {
                state.branches = [...action.payload];
                state.branchesMetadata = adaptBranchesToSelector(action.payload);
                state.isBranchesLoading = false;
                state.error = undefined;
                // if (action.payload.length === 1) {
                //      state.selectedBranch = action.payload[0].branchId;
                // }
            })
            .addCase(fetchBranches.rejected, (state, { error }) => {
                state.branches = [];
                state.branchesMetadata = [];
                state.isBranchesLoading = false;
                state.error = error.message;
            })
            .addCase(fetchDoctors.pending, (state) => {
                state.isDoctorsLoading = true;
                state.error = undefined;
            })
            .addCase(fetchDoctors.fulfilled, (state, action) => {
                let doctors = [...action.payload];
                const {
                    selectedSpecialization,
                    selectedBranch,
                } = state;
                if (selectedBranch) {
                    doctors = filterDoctorsBySelectedBranch(doctors, selectedBranch);
                }
                const specializations = getSpecializationsFromDoctors(doctors);
                if (selectedSpecialization) {
                    doctors = filterDoctorsBySelectedSpecialization(doctors, selectedSpecialization);
                }
                state.doctors = [...action.payload];
                state.doctorsMetadata = adaptDoctorsToSelector(doctors);
                state.specializations = specializations;
                state.specializationsMetadata = adaptSpecializationsToSelector(specializations);
                state.isDoctorsLoading = false;
                state.error = undefined;
            })
            .addCase(fetchDoctors.rejected, (state, { error }) => {
                state.doctors = [];
                state.doctorsMetadata = [];
                state.specializations = [];
                state.specializationsMetadata = [];
                state.isDoctorsLoading = false;
                state.error = error.message;
            });
    },
});

export const {
    selectBranch,
    selectDoctor,
    selectSpecialization,
    selectDate,
    selectProfileOption,
    selectBranchSimplified,
    resetSelectedFilters,
} = filtersSlice.actions;

export default filtersSlice.reducer;
